/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { companyLogoItem, pageStructureStyles, zoneTitle } from "@eisinc/App/SharedStyles/GeneralStyles";
import { screen } from "@eisinc/XDL/Helpers";
import { contentModeCookieName } from "@insite/client-framework/Common/ContentMode";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadQuoteIfNeeded from "@insite/client-framework/Store/Pages/RfqConfirmation/Handlers/LoadQuoteIfNeeded";
import updateQuoteParameter from "@insite/client-framework/Store/Pages/RfqRequestQuote/Handlers/UpdateQuoteParameter";
import translate from "@insite/client-framework/Translate";
import { MetaDescription, MetaKeywords } from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import AddToListModal from "@insite/content-library/Components/AddToListModal";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridContainer, GridItem } from "@insite/mobius/index";
import Page from "@insite/mobius/Page";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { search } = getLocation(state);
    const parsedQuery = parseQueryString<{ quoteId?: string }>(search);
    const quoteId = parsedQuery.quoteId;
    return {
        quoteId,
    };
};

const mapDispatchToProps = {
    loadQuoteIfNeeded,
    updateQuoteParameter,
};

type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqConfirmationPageStyles {
    infoGridItem?: GridItemProps;
    repInfoGridItem?: GridItemProps;
    notes?: InjectableCss;
    orderLines?: GridItemProps;
    productLinesNotes?: InjectableCss;
}

const styles: RfqConfirmationPageStyles = {
    infoGridItem: {
        width: 12,
    },
    repInfoGridItem: {
        width: 12,
    },
    productLinesNotes: {
        css: css`
            display: flex;
            flex-flow: row wrap;
            width: 100%;
        `,
    },
    notes: {
        css: css`
            width: 33.333333%;
            padding: 15px;

            ${screen("lte", "lg")`
                width: 100%;
                order: -1;
            `};
        `,
    },
    orderLines: {
        css: css`
            width: 66.666667%;
            padding: 15px;
            ${screen("lte", "lg")`width: 100%`};
        `,
    },
};

const RfqConfirmationPage = ({ id, quoteId, loadQuoteIfNeeded, updateQuoteParameter }: Props) => {
    const isCmsEditing = getCookie(contentModeCookieName) === "Editing";

    useEffect(() => {
        if (quoteId) {
            loadQuoteIfNeeded({ quoteId });
        }
        // clear the requisition & comment fields on quote creation, when doing this on RFQRequestQuote onSuccess call, it won't update them since the cart is being updated on quoteCreation
        updateQuoteParameter({
            quoteId: "",
            quoteType: "",
            accountId: "",
            jobName: "",
            note: "",
            requisitionNumber: "",
        } as any);
    }, []);

    return (
        <Page data-test-selector="rfqConfirmation">
            <GridContainer>
                {isCmsEditing && <Typography {...zoneTitle}>Header Zone</Typography>}
                <GridItem {...pageStructureStyles.headerGridItem}>
                    <StyledWrapper {...companyLogoItem}>
                        <img src={siteMessage("Company_Logo") as any} alt={translate("Company Logo")} />
                    </StyledWrapper>
                    <Zone contentId={id} zoneName="HeaderWidget" />
                </GridItem>
                <GridItem {...pageStructureStyles.contentOneGridItem}>
                    <GridContainer>
                        {isCmsEditing && <Typography {...zoneTitle}>Details Zone</Typography>}
                        <GridItem {...styles.infoGridItem}>
                            <Zone contentId={id} zoneName="InfoWidget" />
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem {...pageStructureStyles.contentTwoGridItem}>
                    <GridContainer>
                        {isCmsEditing && <Typography {...zoneTitle}>Rep Info Zone</Typography>}
                        <GridItem {...styles.repInfoGridItem}>
                            <Zone contentId={id} zoneName="RepInfoWidget" />
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <StyledWrapper {...styles.productLinesNotes}>
                    <StyledWrapper {...styles.orderLines}>
                        {isCmsEditing && <Typography {...zoneTitle}>Product List Zone</Typography>}
                        <Zone contentId={id} zoneName="InvoiceLinesWidget" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.notes}>
                        {isCmsEditing && <Typography {...zoneTitle}>Notes Zone</Typography>}
                        <Zone contentId={id} zoneName="NotesWidget" />
                    </StyledWrapper>
                </StyledWrapper>
            </GridContainer>
            <AddToListModal />
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RfqConfirmationPage),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
        // XNG-Change
        fieldDefinitions: [MetaKeywords, MetaDescription],
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "RfqConfirmationPage" instead of this constant.
 */
export const RfqConfirmationPageContext = "RfqConfirmationPage";
